/**
 * Announcements List Screen
 * 
 * Displays all announcements with filtering and search
 * Managers see all announcements, users see only their targeted announcements
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  Plus,
  Search,
  RefreshCw,
  Megaphone,
  Edit,
  Trash2,
  Archive,
  Filter
} from 'lucide-react';
import { useAnnouncements } from '../../hooks/useAnnouncements';
import { useAuth } from '../../contexts/AuthContext';
import { AnnouncementCard } from '../../components/announcements/AnnouncementCard';
import { AnnouncementForm } from '../../components/announcements/AnnouncementForm';
import { AnnouncementService } from '../../services/AnnouncementService';
import { NotificationTriggerService } from '../../services/NotificationTriggerService';
import { userService } from '../../services/UserService';
import { Announcement, AnnouncementFormData, AnnouncementPriority, AnnouncementStatus } from '../../@types/announcement';
import { ThemeColors, Spacing, Typography, BorderRadius, Shadows } from '../../styles/ThemeStyles';
import { PageContainer, MainContent } from '../../styles/StyledComponents';

const Header = styled.div`
  margin-bottom: ${Spacing.xl};
`;

const Title = styled.h1`
  font-size: ${Typography.fontSize.xxxl};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  margin-bottom: ${Spacing.md};
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${Spacing.md};
  flex-wrap: wrap;
  margin-bottom: ${Spacing.xl};
`;

const ToolbarSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.md};
  flex-wrap: wrap;
`;

const SearchBar = styled.div`
  position: relative;
  min-width: 300px;

  svg {
    position: absolute;
    left: ${Spacing.md};
    top: 50%;
    transform: translateY(-50%);
    color: ${ThemeColors.text.secondary};
    width: 18px;
    height: 18px;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${Spacing.sm} ${Spacing.md} ${Spacing.sm} 40px;
  border: 1px solid ${ThemeColors.border.medium};
  border-radius: ${BorderRadius.lg};
  font-size: ${Typography.fontSize.sm};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
    box-shadow: 0 0 0 3px ${ThemeColors.primary.light};
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  display: inline-flex;
  align-items: center;
  gap: ${Spacing.sm};
  padding: ${Spacing.sm} ${Spacing.md};
  border: none;
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => props.variant === 'primary' ? `
    background: linear-gradient(135deg, ${ThemeColors.primary.main}, ${ThemeColors.primary.dark});
    color: white;
    box-shadow: ${Shadows.sm};
    
    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: ${Shadows.md};
    }
    
    &:disabled {
      background: ${ThemeColors.gray[400]};
      cursor: not-allowed;
      transform: none;
    }
  ` : `
    background: white;
    color: ${ThemeColors.text.primary};
    border: 1px solid ${ThemeColors.border.medium};
    
    &:hover {
      background: ${ThemeColors.background.default};
      border-color: ${ThemeColors.primary.main};
    }
  `}

  svg {
    width: 16px;
    height: 16px;
  }
`;

const FilterChips = styled.div`
  display: flex;
  gap: ${Spacing.sm};
  flex-wrap: wrap;
  margin-bottom: ${Spacing.lg};
`;

const FilterChip = styled.button<{ active?: boolean }>`
  padding: ${Spacing.xs} ${Spacing.md};
  border: 1px solid ${props => props.active ? ThemeColors.primary.main : ThemeColors.border.medium};
  background: ${props => props.active ? ThemeColors.primary.light : 'white'};
  color: ${props => props.active ? ThemeColors.primary.main : ThemeColors.text.primary};
  border-radius: 20px;
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${ThemeColors.primary.main};
    background: ${ThemeColors.primary.light};
    color: ${ThemeColors.primary.main};
  }
`;

const AnnouncementsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: ${Spacing.lg};
  margin-bottom: ${Spacing.xl};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${Spacing.xxl} ${Spacing.lg};
  background: white;
  border-radius: ${BorderRadius.xl};
  box-shadow: ${Shadows.sm};
`;

const EmptyIcon = styled.div`
  width: 80px;
  height: 80px;
  margin: 0 auto ${Spacing.lg};
  background: ${ThemeColors.primary.light};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${ThemeColors.primary.main};

  svg {
    width: 40px;
    height: 40px;
  }
`;

const EmptyTitle = styled.h3`
  font-size: ${Typography.fontSize.xl};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  margin-bottom: ${Spacing.sm};
`;

const EmptyText = styled.p`
  font-size: ${Typography.fontSize.md};
  color: ${ThemeColors.text.secondary};
  margin-bottom: ${Spacing.lg};
`;

export const AnnouncementsListScreen: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, userRole } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [priorityFilter, setPriorityFilter] = useState<AnnouncementPriority | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<AnnouncementStatus | 'all'>('all');
  const [showForm, setShowForm] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState<Announcement | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Memoize filters to prevent infinite re-renders
  const announcementFilters = useMemo(() => ({
    priority: priorityFilter !== 'all' ? priorityFilter : undefined,
    status: statusFilter !== 'all' ? statusFilter : (userRole === 'manager' ? undefined : 'ACTIVE')
  }), [priorityFilter, statusFilter, userRole]);

  const {
    announcements,
    loading,
    error,
    fetchAnnouncements,
    createAnnouncement,
    updateAnnouncement,
    deleteAnnouncement,
    archiveAnnouncement,
    refreshAnnouncements
  } = useAnnouncements(announcementFilters);

  const filteredAnnouncements = announcements.filter(announcement => {
    const matchesSearch = searchTerm === '' || 
      announcement.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      announcement.content.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const handleCreate = async (data: AnnouncementFormData) => {
    if (!user) return;

    try {
      setIsSubmitting(true);

      // Create announcement first (without attachment URL)
      const announcementData: any = {
        title: data.title,
        content: data.content,
        priority: data.priority,
        detailsLink: data.detailsLink || null,
        expiresAt: data.expiresAt || null,
        targetAudience: data.targetAudience
      };

      const announcementId = await AnnouncementService.createAnnouncement(
        announcementData,
        user.id,
        user.username || user.email || 'Unknown'
      );

      // Upload attachment if provided (after we have the announcement ID)
      if (data.attachment) {
        const uploadResult = await AnnouncementService.uploadAttachment(
          data.attachment,
          announcementId, // Use actual announcement ID
          user.email || user.id
        );

        // Update announcement with attachment info
        await AnnouncementService.updateAnnouncement(announcementId, {
          attachmentUrl: uploadResult.url,
          attachmentName: uploadResult.name,
          attachmentType: uploadResult.type
        });
      }

      // Get target user IDs for notifications
      const targetUserIds: string[] = [];
      if (data.targetAudience.allUsers) {
        const allUsers = await userService.getAllUsers();
        targetUserIds.push(...allUsers.map(u => u.id));
      } else {
        if (data.targetAudience.roles) {
          for (const role of data.targetAudience.roles) {
            const users = await userService.getUsersByRole(role as any);
            targetUserIds.push(...users.map(u => u.id));
          }
        }
        if (data.targetAudience.userIds) {
          targetUserIds.push(...data.targetAudience.userIds);
        }
        // Note: Sales groups and provinces would require additional logic
      }

      // Send notifications
      if (targetUserIds.length > 0) {
        await NotificationTriggerService.triggerAnnouncementCreatedNotification(
          announcementId,
          data.title,
          targetUserIds
        );
      }

      setShowForm(false);
      await refreshAnnouncements();
    } catch (error: any) {
      console.error('Error creating announcement:', error);
      alert('Failed to create announcement: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = async (data: AnnouncementFormData) => {
    if (!editingAnnouncement) return;

    try {
      setIsSubmitting(true);
      
      // Handle attachment update if needed
      let attachmentUrl = editingAnnouncement.attachmentUrl;
      let attachmentName = editingAnnouncement.attachmentName;
      let attachmentType = editingAnnouncement.attachmentType;

      if (data.attachment) {
        const uploadResult = await AnnouncementService.uploadAttachment(
          data.attachment,
          editingAnnouncement.id,
          user?.email || user?.id || ''
        );
        attachmentUrl = uploadResult.url;
        attachmentName = uploadResult.name;
        attachmentType = uploadResult.type;
      }

      await updateAnnouncement(editingAnnouncement.id, {
        ...data,
        attachmentUrl,
        attachmentName,
        attachmentType
      });

      setShowForm(false);
      setEditingAnnouncement(null);
      await refreshAnnouncements();
    } catch (error: any) {
      console.error('Error updating announcement:', error);
      alert('Failed to update announcement: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this announcement?')) return;

    try {
      await deleteAnnouncement(id);
      await refreshAnnouncements();
    } catch (error: any) {
      console.error('Error deleting announcement:', error);
      alert('Failed to delete announcement: ' + error.message);
    }
  };

  const handleArchive = async (id: string) => {
    try {
      await archiveAnnouncement(id);
      await refreshAnnouncements();
    } catch (error: any) {
      console.error('Error archiving announcement:', error);
      alert('Failed to archive announcement: ' + error.message);
    }
  };

  return (
    <PageContainer isMenuCollapsed={false}>
      <MainContent>
        <Header>
          <Title>
            <Megaphone style={{ display: 'inline-block', marginRight: Spacing.sm }} />
            Announcements
          </Title>
        </Header>

        <Toolbar>
          <ToolbarSection>
            <SearchBar>
              <Search />
              <SearchInput
                type="text"
                placeholder="Search announcements..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </SearchBar>
          </ToolbarSection>

          <ToolbarSection>
            {userRole === 'manager' && (
              <Button variant="primary" onClick={() => {
                setEditingAnnouncement(null);
                setShowForm(true);
              }}>
                <Plus />
                Create Announcement
              </Button>
            )}
            <Button variant="secondary" onClick={refreshAnnouncements} disabled={loading}>
              <RefreshCw />
              Refresh
            </Button>
          </ToolbarSection>
        </Toolbar>

        <FilterChips>
          <FilterChip
            active={priorityFilter === 'all'}
            onClick={() => setPriorityFilter('all')}
          >
            All Priorities
          </FilterChip>
          <FilterChip
            active={priorityFilter === 'LOW'}
            onClick={() => setPriorityFilter('LOW')}
          >
            Low
          </FilterChip>
          <FilterChip
            active={priorityFilter === 'MEDIUM'}
            onClick={() => setPriorityFilter('MEDIUM')}
          >
            Medium
          </FilterChip>
          <FilterChip
            active={priorityFilter === 'HIGH'}
            onClick={() => setPriorityFilter('HIGH')}
          >
            High
          </FilterChip>
          <FilterChip
            active={priorityFilter === 'CRITICAL'}
            onClick={() => setPriorityFilter('CRITICAL')}
          >
            Critical
          </FilterChip>
          {userRole === 'manager' && (
            <>
              <FilterChip
                active={statusFilter === 'all'}
                onClick={() => setStatusFilter('all')}
              >
                All Status
              </FilterChip>
              <FilterChip
                active={statusFilter === 'ACTIVE'}
                onClick={() => setStatusFilter('ACTIVE')}
              >
                Active
              </FilterChip>
              <FilterChip
                active={statusFilter === 'ARCHIVED'}
                onClick={() => setStatusFilter('ARCHIVED')}
              >
                Archived
              </FilterChip>
            </>
          )}
        </FilterChips>

        {loading && (
          <div style={{ textAlign: 'center', padding: Spacing.xxl }}>
            Loading announcements...
          </div>
        )}

        {error && (
          <div style={{ 
            padding: Spacing.lg, 
            background: ThemeColors.error.light, 
            color: ThemeColors.error.main,
            borderRadius: BorderRadius.md,
            marginBottom: Spacing.lg
          }}>
            Error: {error}
          </div>
        )}

        {!loading && filteredAnnouncements.length === 0 && (
          <EmptyState>
            <EmptyIcon>
              <Megaphone />
            </EmptyIcon>
            <EmptyTitle>No Announcements Found</EmptyTitle>
            <EmptyText>
              {userRole === 'manager'
                ? 'Create your first announcement to get started.'
                : 'There are no announcements available at this time.'}
            </EmptyText>
            {userRole === 'manager' && (
              <Button variant="primary" onClick={() => setShowForm(true)}>
                <Plus />
                Create Announcement
              </Button>
            )}
          </EmptyState>
        )}

        {!loading && filteredAnnouncements.length > 0 && (
          <AnnouncementsGrid>
            {filteredAnnouncements.map((announcement) => (
              <div key={announcement.id} style={{ position: 'relative' }}>
                <AnnouncementCard
                  announcement={announcement}
                  onView={(id) => navigate(`/announcements/${id}`)}
                />
                {userRole === 'manager' && (
                  <div style={{
                    position: 'absolute',
                    top: Spacing.sm,
                    right: Spacing.sm,
                    display: 'flex',
                    gap: Spacing.xs
                  }}>
                    <Button
                      variant="secondary"
                      onClick={() => {
                        setEditingAnnouncement(announcement);
                        setShowForm(true);
                      }}
                      style={{ padding: Spacing.xs }}
                    >
                      <Edit size={14} />
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => handleArchive(announcement.id)}
                      style={{ padding: Spacing.xs }}
                    >
                      <Archive size={14} />
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => handleDelete(announcement.id)}
                      style={{ padding: Spacing.xs }}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </AnnouncementsGrid>
        )}

        {showForm && (
          <AnnouncementForm
            announcement={editingAnnouncement || undefined}
            mode={editingAnnouncement ? 'edit' : 'create'}
            isLoading={isSubmitting}
            onSubmit={editingAnnouncement ? handleEdit : handleCreate}
            onCancel={() => {
              setShowForm(false);
              setEditingAnnouncement(null);
            }}
          />
        )}
      </MainContent>
    </PageContainer>
  );
};

